import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/lib/ui/Button';
import { Card } from '@/lib/ui/Card';
import { Select } from '@/lib/ui/Select';
import { TextInput } from '@/lib/ui/Field';
import {
  SOCIAL_PLATFORMS,
  type SocialLink,
  type SocialPlatformError,
  type SocialRowErrors,
  type SocialUrlError,
} from './social';

/** An editor row: the wire `{platform,url}` pair plus a stable client-side React key. */
export interface SocialRow extends SocialLink {
  id: string;
}

let rowSeq = 0;
export function toSocialRows(links: SocialLink[]): SocialRow[] {
  return links.map((l) => ({ ...l, id: `social-${++rowSeq}` }));
}
function newRow(): SocialRow {
  return { platform: '', url: '', id: `social-${++rowSeq}` };
}

const PLATFORM_ERROR_KEY: Record<SocialPlatformError, string> = {
  required: 'settings.social.errPlatformRequired',
  duplicate: 'settings.social.errPlatformDuplicate',
};
const URL_ERROR_KEY: Record<SocialUrlError, string> = {
  required: 'settings.social.errUrlRequired',
  invalid: 'settings.social.errUrlInvalid',
  https: 'settings.social.errUrlHttps',
};

/**
 * Edits `site_settings.social`: an ordered list of platform + profile-URL rows. Order is
 * the footer's display order, so rows can be moved up/down. Errors on a field show once
 * the field was left (blur) or a save was attempted (`showAllErrors`).
 */
export function SocialLinksEditor({
  rows,
  errors,
  showAllErrors,
  onChange,
}: {
  rows: SocialRow[];
  errors: SocialRowErrors[];
  showAllErrors: boolean;
  onChange: (rows: SocialRow[]) => void;
}) {
  const { t } = useTranslation();
  const [touched, setTouched] = useState<Set<string>>(() => new Set());
  const touch = (key: string) =>
    setTouched((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));

  const platformOptions = (current: string) => {
    const known = SOCIAL_PLATFORMS.map((p) => ({
      value: p,
      label: t(`settings.social.platforms.${p}`),
    }));
    // Keep a platform stored outside the known list selectable rather than silently
    // replacing it (e.g. a value written by the seed or the API).
    const cur = current.trim().toLowerCase();
    return cur && !(SOCIAL_PLATFORMS as readonly string[]).includes(cur)
      ? [...known, { value: current, label: current }]
      : known;
  };

  const update = (i: number, patch: Partial<SocialLink>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <Card>
      <div className="flex flex-col gap-md">
        <h2 className="text-title text-ink-deep">{t('settings.social.heading')}</h2>
        <p className="text-small text-ink-mute">{t('settings.social.description')}</p>

        {rows.length === 0 ? (
          <p className="text-small text-ink-mute">{t('settings.social.empty')}</p>
        ) : (
          <ol className="flex flex-col gap-sm">
            {rows.map((row, i) => {
              const n = i + 1;
              const e = errors[i] ?? {};
              const showPlatform = showAllErrors || touched.has(`${row.id}:platform`);
              const showUrl = showAllErrors || touched.has(`${row.id}:url`);
              // A duplicate is a cross-row conflict: show it as soon as both rows have a value.
              const platformError =
                e.platform && (showPlatform || e.platform === 'duplicate')
                  ? t(PLATFORM_ERROR_KEY[e.platform])
                  : null;
              const urlError = e.url && showUrl ? t(URL_ERROR_KEY[e.url]) : null;
              return (
                <li
                  key={row.id}
                  aria-label={t('settings.social.rowLabel', { n })}
                  className="flex flex-col gap-sm rounded-sm border-[0.5px] border-hairline p-sm sm:flex-row sm:items-start"
                >
                  <div className="flex shrink-0 gap-xs sm:flex-col sm:pt-[22px]">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={t('settings.social.moveUp', { n })}
                      disabled={i === 0}
                      onClick={() => move(i, -1)}
                    >
                      ↑
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={t('settings.social.moveDown', { n })}
                      disabled={i === rows.length - 1}
                      onClick={() => move(i, 1)}
                    >
                      ↓
                    </Button>
                  </div>
                  <div className="w-full sm:w-[200px] sm:shrink-0">
                    <Select
                      label={t('settings.social.platform')}
                      value={row.platform || undefined}
                      onValueChange={(v) => {
                        touch(`${row.id}:platform`);
                        update(i, { platform: v });
                      }}
                      options={platformOptions(row.platform)}
                      required
                      error={platformError}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <TextInput
                      label={t('settings.social.url')}
                      value={row.url}
                      placeholder={t('settings.social.urlPlaceholder')}
                      onChange={(ev) => update(i, { url: ev.target.value })}
                      onBlur={() => touch(`${row.id}:url`)}
                      type="url"
                      inputMode="url"
                      dir="ltr"
                      autoComplete="off"
                      spellCheck={false}
                      required
                      error={urlError}
                    />
                  </div>
                  <div className="shrink-0 sm:pt-[22px]">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={t('settings.social.remove', { n })}
                      onClick={() => remove(i)}
                    >
                      {t('common.delete')}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        <div>
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange([...rows, newRow()])}>
            {t('settings.social.add')}
          </Button>
        </div>
      </div>
    </Card>
  );
}
